import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { AccountAlreadyExistsError, signUp } from '@/lib/api-client';
import { formatBrPhone } from '@/lib/format-br-phone';
import {
  CreateAccountFormSchema,
  type CreateAccountFormValues,
} from '@/pages/auth/create-account.schema';
import {
  AUTH_INPUT_CLASS,
  AuthPageLayout,
} from '@/pages/auth/auth-page-layout';
import { useI18nContext } from '@/i18n/i18n-react';

const MotionLink = motion.create(Link);

export function CreateAccountPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { LL } = useI18nContext();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateAccountFormValues>({
    resolver: zodResolver(CreateAccountFormSchema),
    defaultValues: { name: '', phone: '', email: '', storeName: '' },
  });

  const onSubmit = async (values: CreateAccountFormValues) => {
    setIsSubmitting(true);

    try {
      await signUp({
        email: values.email,
        name: values.name,
        phone: values.phone.replace(/\D/g, ''),
        storeName: values.storeName,
      });
      navigate(`/login/success?email=${encodeURIComponent(values.email)}`);
    } catch (error) {
      toast.error(LL.general.error(), {
        description:
          error instanceof AccountAlreadyExistsError
            ? LL.createAccount.accountAlreadyExists()
            : error instanceof Error
              ? error.message
              : LL.createAccount.failedToCreateAccount(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthPageLayout.Header
        title={LL.createAccount.title()}
        subtitle={LL.createAccount.subtitle()}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <AuthPageLayout.Content>
          <div className="space-y-2">
            <Label htmlFor="name" className="font-semibold text-[#15191E]">
              {LL.createAccount.nameLabel()}
            </Label>
            <Input
              className={AUTH_INPUT_CLASS}
              id="name"
              placeholder={LL.createAccount.namePlaceholder()}
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="font-semibold text-[#15191E]">
              {LL.createAccount.phoneLabel()}
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  className={AUTH_INPUT_CLASS}
                  {...field}
                  id="phone"
                  inputMode="tel"
                  placeholder={LL.createAccount.phonePlaceholder()}
                  onChange={(e) =>
                    field.onChange(formatBrPhone(e.target.value))
                  }
                />
              )}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="font-semibold text-[#15191E]">
              {LL.createAccount.emailLabel()}
            </Label>
            <Input
              className={AUTH_INPUT_CLASS}
              id="email"
              type="email"
              placeholder={LL.createAccount.emailPlaceholder()}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="storeName" className="font-semibold text-[#15191E]">
              {LL.createAccount.storeNameLabel()}
            </Label>
            <Input
              className={AUTH_INPUT_CLASS}
              id="storeName"
              placeholder={LL.createAccount.storeNamePlaceholder()}
              {...register('storeName')}
            />
            {errors.storeName && (
              <p className="text-sm text-destructive">
                {errors.storeName.message}
              </p>
            )}
          </div>
          <AuthPageLayout.Submit disabled={isSubmitting}>
            {isSubmitting
              ? LL.createAccount.submitting()
              : LL.createAccount.submitButton()}
          </AuthPageLayout.Submit>
        </AuthPageLayout.Content>
      </form>
      <AuthPageLayout.Footer>
        <p className="text-sm text-[#4B5361]">
          {LL.createAccount.alreadyHaveAccountQuestion()}{' '}
          <MotionLink
            to="/login"
            className="inline-block font-semibold text-[#155DFC]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {LL.createAccount.signInLink()}
          </MotionLink>
        </p>
      </AuthPageLayout.Footer>
    </AuthPageLayout>
  );
}
